import {useEffect, useState} from 'react';
import {useCookies} from "react-cookie";

/**
 * Offline (deferred NFC) risk policies and exchange rates (PYMNT-2590 NFC Ground Offline, design 5.4).
 * Backed by /backoffice/offline-policies and /backoffice/offline-policies/forex-rates.
 *
 * Policies are immutable rows: every PUT creates a new id and retires the row it replaces, so after
 * each write the whole list is reloaded rather than patched. The unfiltered GET returns every current
 * (non-retired) policy across airlines, with tail limits already resolved against the airline policy
 * and `overriddenLimits` naming the ones the tail sets itself.
 */
function useOfflinePolicies() {
    const [policies, setPolicies] = useState([]);
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiRoot = process.env.REACT_APP_API_ROOT_URL;
    const [cookies] = useCookies(['idToken']);
    const apiHeaders = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + cookies.idToken
    }

    const readError = async (response, fallback) => {
        try {
            const body = await response.json();
            if (body.errorType === 'POLICY_BASELINE_MISSING') {
                return 'The airline policy must exist before a tail override can be saved';
            }
            if (body.errorType === 'POLICY_IN_USE') {
                return 'Retire the tail overrides of this airline before retiring its airline policy';
            }
            return body.message || body.errorType || fallback;
        } catch (e) {
            return fallback;
        }
    };

    const getPolicies = async () => {
        try {
            setLoading(true);
            setError(null);
            const [policyResponse, rateResponse] = await Promise.all([
                fetch(`${apiRoot}/backoffice/offline-policies`, {headers: apiHeaders}),
                fetch(`${apiRoot}/backoffice/offline-policies/forex-rates`, {headers: apiHeaders}),
            ]);
            if (!policyResponse.ok || !rateResponse.ok) {
                throw new Error('Network response was not ok');
            }
            setPolicies(await policyResponse.json());
            setRates(await rateResponse.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Writes an airline policy (tailNumber '') or a tail override; the body is
     * {airlineIcao, tailNumber, currency, limits, enabled, confirmExposureCap}. Returns an error message or null.
     */
    const savePolicy = async (policy) => {
        const response = await fetch(`${apiRoot}/backoffice/offline-policies`, {
            method: 'PUT', headers: apiHeaders, body: JSON.stringify(policy),
        });
        if (!response.ok) {
            return await readError(response, 'Failed to save offline risk policy');
        }
        await getPolicies();
        return null;
    };

    /** Retires a policy: the row is kept for the deferred charges that carry its id but no longer served. */
    const retirePolicy = async (policyId) => {
        const response = await fetch(`${apiRoot}/backoffice/offline-policies/${policyId}`, {
            method: 'DELETE', headers: apiHeaders,
        });
        if (!response.ok) {
            if (response.status === 404) {
                await getPolicies();
                return 'This policy has already been replaced or retired; the list was refreshed';
            }
            return await readError(response, 'Failed to retire offline risk policy');
        }
        await getPolicies();
        return null;
    };

    /** Upserts one exchange rate: {iso, rate, name}. */
    const saveRate = async (rate) => {
        const response = await fetch(`${apiRoot}/backoffice/offline-policies/forex-rates`, {
            method: 'PUT', headers: apiHeaders, body: JSON.stringify(rate),
        });
        if (!response.ok) {
            return await readError(response, 'Failed to save exchange rate');
        }
        await getPolicies();
        return null;
    };

    useEffect(() => {
        getPolicies();
    }, []);

    return {policies, rates, loading, error, setError, getPolicies, savePolicy, retirePolicy, saveRate};
}

export default useOfflinePolicies;
